import { useContext, useEffect, useState } from "react"
import { GroupContext } from "../contexts/group"
import { UserContext } from "../contexts/user"
import { createAnswer, getExtraAnswerBySlug, getReportInfoTIM, updateAnswer } from "../axios/axiosFunctions";
import { Alert, Box, Button, Grid, List, ListItem, ListItemIcon, ListItemText, TextField, Typography } from "@mui/material";
import CommentIcon from '@mui/icons-material/Comment';
import SaveIcon from '@mui/icons-material/Save';
import { ExportTIMChartV } from "./ExportTIMChartV.jsx";
import SaveReminder from "./SaveReminder";
import { categoryForSlug } from "../utils/timFamily";
import EditableText from "./EditableText";
import VerbatimComments from "./VerbatimComments";

const BlockReportTIM = ({slug,blockId,color,info, onQuestionsDataReady,onFeedbackReady, groupId, assessmentSlug, reportUserId, onReflectionReady, readOnly}) => {

    const [currentBlockText, setCurrentBlockText] = useState("");
    const {currentGroup} = useContext(GroupContext);
    const {currentUser} = useContext(UserContext);
    const effectiveGroupId = groupId || currentGroup?._id;
    const [questions, setQuestions] = useState([]);
    const [blockTotal, setBlockTotal] = useState([]);
    const [feedbackAnsw, setFeedbackAnsw] = useState([]);

    const isAdminPreview = !!reportUserId || !!readOnly;
    // Executive report tweaks (per client feedback): rename the senior-block
    // header to "Organizational Perspective", drop its descriptive blurbs, and
    // hide the per-section reflection prompt. TIM reports are unaffected.
    const isExecutive = categoryForSlug(assessmentSlug) === "executive";
    const isSeniorBlock = slug === "senior_leader_survey_questions";
    // Group-scoped slug: any team viewer sees and edits the same reflection.
    // (Previously user-scoped — caused per-user silos and broke admin PDFs.)
    const reflectionSlug = assessmentSlug && effectiveGroupId && blockId
        ? `${assessmentSlug}_block_reflection_${effectiveGroupId}_${blockId}`
        : null;
    const [reflectionText, setReflectionText] = useState("");
    const [defaultReflection, setDefaultReflection] = useState(null);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMsg, setAlertMsg] = useState("");
    const [alertSeverity, setAlertSeverity] = useState("error");

    useEffect(() => {
        const callInfo = async()=>{
            const res = await getReportInfoTIM(effectiveGroupId,blockId);
            if(res){
                const blockInfo = [res.data.pop()];
                const data = res.data.map((question)=>({
                    positive: Number(question.positivePercentage),
                    negative: Number(question.negativePercentage),
                    question: question.text
                }));
                setQuestions(data);
                if(onQuestionsDataReady) {
                    onQuestionsDataReady(data);
                }
                if(blockInfo[0].opAnswers){
                    setFeedbackAnsw(blockInfo[0].opAnswers);
                    if(onFeedbackReady) {
                        onFeedbackReady(blockInfo[0].opAnswers);
                    }
                }
                const data2 = blockInfo.map(info=>({
                    positive: Number(info.positivePercentage),
                    negative: Number(info.negativePercentage),
                    name:info.name
                }))
                setBlockTotal(data2);
            }
        }
        callInfo();
    }, [blockId, effectiveGroupId]);

    useEffect(() => {
        const infoBlock = info.find((b)=>b.block === blockId);
        setCurrentBlockText(infoBlock?.text || "");
    }, [blockId,info]);

    useEffect(() => {
        if (!reflectionSlug) return;
        let cancelled = false;
        (async () => {
            const res = await getExtraAnswerBySlug(reflectionSlug);
            if (cancelled) return;
            if (res?.answer) {
                setDefaultReflection(res.answer);
                setReflectionText(res.answer.answer || "");
                onReflectionReady?.(blockId, res.answer.answer || "");
            } else {
                onReflectionReady?.(blockId, "");
            }
        })();
        return () => { cancelled = true; };
    }, [reflectionSlug, blockId]);

    const handleReflectionChange = (e) => {
        setReflectionText(e.target.value);
    };

    const submitReflection = async () => {
        if (!reflectionSlug || !currentUser?._id) return;
        let res;
        if (defaultReflection) {
            res = await updateAnswer(defaultReflection._id, { answer: reflectionText });
        } else {
            res = await createAnswer({ user: currentUser._id, answer: reflectionText, slug: reflectionSlug });
        }
        if (!res) {
            setAlertMsg("Error while saving please try later");
            setAlertSeverity("error");
            setShowAlert(true);
            setTimeout(() => setShowAlert(false), 4000);
            return;
        }
        if (res.answer) setDefaultReflection(res.answer);
        onReflectionReady?.(blockId, reflectionText);
        setAlertMsg("Saved correctly");
        setAlertSeverity("success");
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 2000);
    };

    const wrapLabel = (s, max = 28) => {
        return s
        .split(' ')
        .reduce((lines, w) => {
            const last = lines[lines.length - 1];
            if (!last || (last + ' ' + w).length > max) lines.push(w);
            else lines[lines.length - 1] = last + ' ' + w;
            return lines;
        }, [])
        .join('\n'); // <- clave: inyecta \n
    };

    return (
    <Box display="flex" flexDirection="column" sx={{padding:{xs:"0px 16px",sm:"0px 24px",md:"0px 48px"}}}>
        <Grid container spacing={2} sx={{margin:"32px 0px 40px 0px"}}>
            {isSeniorBlock && isExecutive && (
            <Grid size={12}>
                <Typography variant="h6" color="secondary" fontWeight={600} sx={{marginBottom:"12px"}}>Organizational Perspective</Typography>
            </Grid>
            )}
            <Grid size={12}>
                <EditableText fieldKey={`block.${blockId}.info`} variant="body1" color="secondary" fontWeight={400} sx={{marginTop:"4px",lineHeight:"1.7",fontSize:"1.05rem",whiteSpace:"pre-wrap"}}>{currentBlockText}</EditableText>
            </Grid>
            {isSeniorBlock && !isExecutive && (
            <Grid size={12}>
                <EditableText fieldKey="senior.perspective.heading" multiline={false} variant="h6" color="secondary" fontWeight={600} sx={{marginBottom:"12px"}}>Senior Leader Perspective</EditableText>
                <EditableText fieldKey="senior.perspective.intro" variant="body1" color="secondary" fontWeight={400} sx={{lineHeight:"1.7",fontSize:"1.05rem"}}>This section provides a complementary view of team effectiveness through the eyes of senior leaders. It reflects their assessment of the team's quality, efficiency, innovation, decision escalation, and overall alignment with organizational expectations. This perspective adds a critical external lens, ensuring the team understands how its performance is perceived by those who rely on its results and leadership impact. </EditableText>
            </Grid>
            )}
            <Grid size={12}>
                <Box display="flex" alignItems="center" flexDirection="column" mb={4}>
                    {questions && questions.length > 0 && (
                        <Box sx={{ width: "100%" }}>
                            <ExportTIMChartV
                                questions={questions}
                                positiveColor="#69AF45"
                                negativeColor="#2B8DCC"
                            />
                        </Box>
                    )}
                    <Box sx={{marginTop:"24px"}}>
                    {
                        questions.map((q,i)=>(
                            <Typography key={i} variant="body1" color="secondary" fontWeight={400} sx={{fontSize:"1.05rem",lineHeight:"1.7"}}>Q{i+1}. {q.question}</Typography>
                        ))
                    }
                    </Box>
                </Box>
                {!isExecutive && (
                <>
                <Typography variant="body1" color="secondary" fontWeight={600} sx={{marginTop:"24px", marginBottom:"16px",fontSize:"1.05rem"}}>Reflection: Which of the items in this dimension resonate most as a high-priority focus area?</Typography>
                <TextField
                    value={reflectionText}
                    onChange={handleReflectionChange}
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Write your reflection here..."
                    disabled={isAdminPreview}
                />
                {showAlert && (
                    <Alert sx={{marginTop:"16px"}} severity={alertSeverity}>{alertMsg}</Alert>
                )}
                {!isAdminPreview && (
                    <Box sx={{display:"flex",flexDirection:"column",alignItems:"flex-start",marginTop:"16px",marginBottom:"24px"}}>
                        <Button onClick={submitReflection} variant="contained" startIcon={<SaveIcon/>}>Save</Button>
                        <SaveReminder align="left"/>
                    </Box>
                )}
                </>
                )}
            </Grid>
            {
                slug === "senior_leader_survey_questions" &&
                <Grid size={12}>
                     <EditableText fieldKey="verbatim.heading" multiline={false} variant="h6" color="secondary" fontWeight={600} sx={{marginTop:"4px",lineHeight:"1.7"}}>Verbatim Comments</EditableText>
                     <VerbatimComments items={feedbackAnsw} />
                </Grid>
            }
            {/* <Grid size={12}>
                <BarChart
                    dataset={blockTotal}
                    xAxis={[{min:0,max:100,tickNumber:5,tickLabelStyle:{fontSize:12},position:"top"}]}
                    yAxis={[{scaleType:"band",dataKey:"name",width:1000,tickLabelStyle:{fontSize:15,fontWeight:400,whiteSpace:"normal",textOverflow:"unset"},}]}
                    series={[{dataKey:"positive",label:`Positive`,valueFormatter:(value)=>`${value}%`,stack:"stack",color:"#69AF45"},{dataKey:"negative",label:"Negative",stack:"stack",valueFormatter:(value)=>`${value}%`,color:"#BC4749"}]}
                    layout="horizontal"
                    grid={{vertical:true}}
                    height={100}
                    margin={{right:100,left:-400}}
                    barLabel={(item)=>{if(item.value === 0) return ""; return `${item.value}%`}}
                    sx={{
                        [`& .${axisClasses.tickLabel}`]: {
                        whiteSpace: 'pre-wrap',
                        overflow: 'visible',
                        textOverflow: 'unset',
                        },
                        [`& .${chartsGridClasses.line}`]: { strokeWidth: 4 },
                    }}
                />
                {blockTotal.length > 0 && onBlockSummaryChartReady && (
                    <div style={{ position: "absolute", left: -9999, top: -9999 }}>
                        <ExportTIMChart
                        data={blockTotal}
                        labelKey="name"
                        positiveColor={"#69AF45"}
                        graphHeight={150}
                        onImageReady={onBlockSummaryChartReady}
                        />
                    </div>
                )}
            </Grid>  */}
        </Grid>
    </Box>
    )
}

export default BlockReportTIM